import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  Button,
  Typography,
  IconButton,
  Card,
  Modal,
  Alert,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DragIndicator from "@mui/icons-material/DragIndicator";
import { Add as AddIcon } from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DSOKProductAddNewBindingModal from "../dsok-products/DSOKProductAddNewBindingModal";

const EXCLUDED_TEMP_KEYS = ["name", "min", "max", "bindingId", "required"];

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "75vw",
  maxHeight: "70vh",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  overflow: "auto",
};

const SortableGroupItem = ({
  code,
  index,
  group,
  onEdit,
  onDelete,
  defaultLanguage,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: code });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "grab",
  };
  const title =
    (group?.name &&
      (group.name[defaultLanguage] || group.name.en || group.name.sk)) ||
    "";
  return (
    <div ref={setNodeRef} style={style}>
      <Box
        sx={{
          p: 1,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator sx={{ color: "text.secondary", fontSize: 20 }} />
          <Typography variant="subtitle1">{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {`min: ${Number(group.min || 0)}  max: ${Number(group.max || 0)}  ${
              group.required ? "• required" : ""
            }`}
          </Typography>
        </Box>
        <Box>
          <IconButton size="small" onClick={() => onEdit(code)}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(code)}>
            <DeleteForeverIcon sx={{ color: "#000" }} />
          </IconButton>
        </Box>
      </Box>
    </div>
  );
};

const CategoryBindingsTab = ({ category, onEditCategory, defaultLanguage }) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = React.useState(); // { type: 'add'|'edit', value: code }

  // Modal values shaped like Formik values expected by DSOKProductAddNewBindingModal
  const [modalValues, setModalValues] = React.useState({
    bindings: { name: {}, min: 0, max: 0, bindingId: "", required: false },
  });

  const setFieldValue = (path, value) => {
    // Supports paths like 'bindings.min', 'bindings.name', 'bindings.bindingId', 'bindings.required'
    if (!path || !path.startsWith("bindings")) return;
    setModalValues((prev) => {
      const next = { ...prev, bindings: { ...(prev.bindings || {}) } };
      const key = path.replace("bindings.", "");
      next.bindings[key] = value;
      return next;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getGroupsArray = React.useCallback(() => {
    const src = category?.bindings || {};
    return Object.keys(src || {})
      .map((key, i) => {
        if (EXCLUDED_TEMP_KEYS.includes(key)) return null;
        const g = src[key] || {};
        const index = g.index === undefined ? i : g.index;
        return { code: key, index, ...g };
      })
      .filter(Boolean)
      .sort((a, b) => a.index - b.index);
  }, [category]);

  const handleAddGroupClick = () => {
    setModalValues({
      bindings: { name: {}, min: 0, max: 0, bindingId: "", required: false },
    });
    setOpenModal({ type: "add", value: null });
  };

  const handleEditGroup = (code) => {
    const g = (category?.bindings && category.bindings[code]) || {};
    setModalValues({
      bindings: {
        name: g.name || {},
        min: g.min || 0,
        max: g.max || 0,
        bindingId: g.bindingId || "",
        required: Boolean(g.required),
      },
    });
    setOpenModal({ type: "edit", value: code });
  };

  const handleDeleteGroup = (code) => {
    const updated = { ...(category || {}) };
    const src = { ...(updated.bindings || {}) };
    delete src[code];
    // Reindex
    const arr = Object.keys(src)
      .filter((k) => !EXCLUDED_TEMP_KEYS.includes(k))
      .map((k) => ({ code: k, ...src[k] }))
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    arr.forEach((g, idx) => {
      src[g.code] = { ...g, index: idx };
      delete src[g.code].code;
    });
    updated.bindings = src;
    onEditCategory(updated);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event || {};
    if (!over || active.id === over.id) return;
    const arr = getGroupsArray();
    const oldIndex = arr.findIndex((g) => g.code === active.id);
    const newIndex = arr.findIndex((g) => g.code === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(arr, oldIndex, newIndex);
    const updated = { ...(category || {}) };
    const src = { ...(updated.bindings || {}) };
    moved.forEach((g, idx) => {
      src[g.code] = { ...g, index: idx };
      delete src[g.code].code;
    });
    updated.bindings = src;
    onEditCategory(updated);
  };

  const handleSaveFromModal = () => {
    const { bindingId, name, min, max, required } = modalValues.bindings || {};
    if (!bindingId) return; // modal itself validates
    const updated = { ...(category || {}) };
    const src = { ...(updated.bindings || {}) };

    const existingCodes = Object.keys(src).filter(
      (k) => !EXCLUDED_TEMP_KEYS.includes(k)
    );

    if (openModal?.type === "edit" && openModal?.value) {
      const code = openModal.value;
      const current = src[code] || {};
      src[code] = {
        ...current,
        bindingId,
        name: { sk: name?.sk || "", en: name?.en || "" },
        min: Number(min || 0),
        max: Number(max || 0),
        required: Boolean(required),
        index: current.index ?? 0,
      };
    } else {
      // Append a new group without removing existing ones
      const nextIndex = existingCodes.length;
      const code = `group-${Date.now()}`;
      src[code] = {
        bindingId,
        name: { sk: name?.sk || "", en: name?.en || "" },
        min: Number(min || 0),
        max: Number(max || 0),
        required: Boolean(required),
        index: nextIndex,
      };
    }
    updated.bindings = src;
    onEditCategory(updated);
    setOpenModal(undefined);
  };

  const groups = getGroupsArray();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        <Alert variant="outlined" severity="info">
          {t("pages.dsokCategories.addBindingsInfo")}
        </Alert>
      </Typography>
      <Div
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={handleAddGroupClick}
        >
          {t("pages.dsokProducts.addBinding", { defaultValue: "Add Binding" })}
        </Button>
      </Div>

      {groups.length ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={groups.map((g) => g.code)}
            strategy={verticalListSortingStrategy}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              {groups.map((g) => (
                <SortableGroupItem
                  key={g.code}
                  code={g.code}
                  index={g.index}
                  group={g}
                  defaultLanguage={defaultLanguage}
                  onEdit={handleEditGroup}
                  onDelete={handleDeleteGroup}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("common.noData", { defaultValue: "No data" })}
        </Typography>
      )}

      <Modal open={!!openModal} onClose={() => setOpenModal(undefined)}>
        <Div sx={modalStyle}>
          {openModal && (
            <DSOKProductAddNewBindingModal
              values={modalValues}
              setFieldValue={setFieldValue}
              onCancel={() => setOpenModal(undefined)}
              onSave={handleSaveFromModal}
              editingId={openModal.type === "edit" ? openModal.value : null}
            />
          )}
        </Div>
      </Modal>
    </Box>
  );
};

export default CategoryBindingsTab;
